import React, { useContext, useEffect, useRef, useState } from "react";

import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import MuteTicketModal from "../MuteTicketModal";
import { isTicketMuted } from "../../utils/ticketMute";
import formatContactDisplayName from "../../utils/formatContactDisplayName";

const TicketOptionsMenu = ({ ticket, menuOpen, handleClose, anchorEl }) => {
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [muteTicketModalOpen, setMuteTicketModalOpen] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = () => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenTransferModal = () => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	const handleOpenMuteModal = () => {
		setMuteTicketModalOpen(true);
		handleClose();
	};

	const handleUnmute = async () => {
		try {
			const { data } = await api.post(`/tickets/${ticket.id}/mute`, {
				muted: false,
			});
			if (data.synced) {
				toast.success(i18n.t("ticketMute.unmutedSynced"));
			} else {
				toast.warn(i18n.t("ticketMute.unmutedLocalOnly"));
			}
		} catch (err) {
			toastError(err);
		} finally {
			handleClose();
		}
	};

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>
				<MenuItem onClick={handleOpenTransferModal}>
					{i18n.t("ticketOptionsMenu.transfer")}
				</MenuItem>
				{isTicketMuted(ticket) ? (
					<MenuItem onClick={handleUnmute}>
						{i18n.t("ticketMute.unmute")}
					</MenuItem>
				) : (
					<MenuItem onClick={handleOpenMuteModal}>
						{i18n.t("ticketMute.mute")}
					</MenuItem>
				)}
				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<MenuItem onClick={handleOpenConfirmationModal}>
							{i18n.t("ticketOptionsMenu.delete")}
						</MenuItem>
					)}
				/>
			</Menu>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
					formatContactDisplayName(ticket.contact)
				}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticketWhatsappId={ticket.whatsappId}
			/>
			<MuteTicketModal
				open={muteTicketModalOpen}
				onClose={() => setMuteTicketModalOpen(false)}
				ticket={ticket}
			/>
		</>
	);
};

export default TicketOptionsMenu;
